import type { Action } from "../actions/types";
import type { RevenueFlagStatus } from "../actions/revenue";

// Whether the Aya Master Return credit matches what Aya should have kept.
export type RevenueMasterStatus =
  | "Ok"
  | "Missing"
  | "Mismatch"
  | "NotApplicable"
  | "NotSettled";

/** One invoice as the revenue screen lists it. Money is in dollars. */
export interface RevenueInvoice {
  invoiceGuid: string;
  invoiceNumber: string;
  invoiceType: string;
  businessGuid: string;
  businessName: string;
  province: string | null;
  paymentStatus: string;
  createdAt: string;
  dueDate: string | null;
  periodStartDate: string | null;
  periodEndDate: string | null;
  settledAt: string | null;
  settledDate: string;
  eftStatus: string | null;
  eftSentAt: string | null;
  eftCompletedAt: string | null;

  passThrough: number;
  adminFee: number;
  pepmFee: number;
  otherFees: number;
  totalRevenue: number;
  taxPremium: number;
  taxRetailSales: number;
  taxHst: number;
  taxGst: number;
  taxOther: number;
  taxTotal: number;
  // Where the tax sits, from the tax ledger: null when it has no line.
  taxAyaMaster: number | null;
  taxBusinessBucket: number | null;
  invoiceTotal: number;
  masterCredit: number;
  masterExpected: number;
  masterStatus: RevenueMasterStatus;

  documentUrl: string | null;
  isExcluded: boolean;
  flagStatus: RevenueFlagStatus | null;
  flagNote: string | null;
  flaggedBy: string | null;
  flaggedByName: string | null;
  flaggedAt: string | null;

  // What the sweeps did with a settled invoice: the sweep that took its
  // revenue, and where its tax stands.
  revenueSweepGuid?: string | null;
  revenueSweepNumber?: string | null;
  revenueSweepStatus?: string | null;
  revenueState?: RevenueMoveState;
  taxState?: RevenueMoveState;
  taxSweeps?: RevenueSweepRef[] | null;
  taxMovedOut?: number;
  taxToMove?: number;
  taxPaidAtSpend?: number;
}

/** Whether an invoice's revenue, or its tax, has left the wallet holding it. */
export type RevenueMoveState = "Moved" | "PartMoved" | "NotMoved" | "NotReceived" | "PaidAtSpend" | "None";

/** A sweep named on an invoice. */
export interface RevenueSweepRef {
  guid: string;
  sweepNumber: string;
  status: string;
  amount: number;
}

/** The totals of a month, a day or a whole list. */
export interface RevenueTotals {
  period: string;
  invoices: number;
  adminFee: number;
  pepmFee: number;
  otherFees: number;
  totalRevenue: number;
  taxPremium: number;
  taxRetailSales: number;
  taxHst: number;
  taxGst: number;
  taxOther: number;
  taxTotal: number;
  taxAyaMaster: number;
  taxBusinessBucket: number;
  passThrough: number;
  invoiceTotal: number;
  masterCredit: number;
  masterExpected: number;
  masterIssues: number;
  flaggedCollected: number;
  flaggedToCollect: number;
  unflagged: number;
  collectedRevenue: number;
  toCollectRevenue: number;
  unflaggedRevenue: number;
  // What the sweeps did: revenue moved out of Aya Master, still there, or
  // not received there; tax moved out, still to move, or paid at spend.
  revenueMovedOut?: number;
  revenueInAyaMaster?: number;
  revenueNotReceived?: number;
  taxMovedOut?: number;
  taxToMove?: number;
  taxPaidAtSpend?: number;
}

export interface RevenueSummary {
  periods: RevenueTotals[];
  total: RevenueTotals;
  excludedInvoices: number;
}

export interface RevenueInvoiceList {
  invoices: RevenueInvoice[];
  total: RevenueTotals;
  excludedInvoices: number;
}

export interface RevenueTaxLine {
  taxType: string;
  taxRate: number;
  taxValue: number;
}

export interface RevenueFeeLine {
  label: string;
  feeType: string;
  feeRate: number;
  quantity: number;
  subtotal: number;
  tax: number;
  total: number;
  taxes: RevenueTaxLine[];
}

export interface RevenueMemberLine {
  className: string;
  bucket: string;
  personGuid: string;
  firstName: string;
  lastName: string;
  province: string;
  contribution: number;
  tax: number;
  total: number;
}

export interface RevenueClaimLine {
  bucket: string;
  claimNumber: string;
  claimType: string;
  firstName: string;
  lastName: string;
  province: string;
  claimAmount: number;
  claimProcessingFee: number;
  tax: number;
  total: number;
}

export interface RevenueLedgerEntry {
  date: string;
  businessGuid: string;
  businessName: string;
  accountType: string;
  transactionCode: string;
  transactionSign: string;
  availableAmount: number;
  holdAmount: number;
  returnAmount: number;
  taxAmount: number;
  description: string;
}

export interface RevenueFlag {
  status: RevenueFlagStatus;
  note: string | null;
  flaggedBy: string;
  flaggedByName: string | null;
  flaggedAt: string;
}

export interface RevenueInvoiceDetail {
  invoice: RevenueInvoice;
  fees: RevenueFeeLine[];
  members: RevenueMemberLine[];
  claims: RevenueClaimLine[];
  ledger: RevenueLedgerEntry[];
  flags: RevenueFlag[];
}

export type RevenueMasterKind =
  | "InvoiceCredit"
  | "OtherCredit"
  | "TransferOut"
  | "SweepOut"
  | "SweepReversal"
  | "CreditMemoTax"
  | "OtherDebit"
  | "TaxBucketMove";

/** One entry on Aya Master's Return bucket. Amount is positive, in dollars. */
export interface RevenueMasterEntry {
  date: string;
  period: string;
  kind: RevenueMasterKind;
  transactionCode: string;
  transactionSign: string;
  amount: number;
  reference: string;
  description: string;
  user: string;
  destinationGuid: string;
  destinationBucket: string;
  destinationName: string;
}

/** What came into and went out of Aya Master's Return bucket in a month. */
export interface RevenueMasterMonth {
  period: string;
  invoiceCredits: number;
  credited: number;
  // credited split into Aya's fee revenue and the tax on those fees.
  revenueCredited: number;
  taxOnFees: number;
  taxOnFeesHst: number;
  taxOnFeesGst: number;
  taxOnFeesPremium: number;
  taxOnFeesOther: number;
  otherCredited: number;
  transfers: number;
  transferredOut: number;
  sweeps: number;
  sweptOut: number;
  revenueSweptOut?: number;
  taxSweptOut?: number;
  creditMemoTax: number;
  otherOut: number;
  net: number;
}

export interface RevenueMasterSummary {
  ayaMasterGuid: string;
  ayaMasterName: string;
  returnBalance: number;
  taxBalance?: number;
  months: RevenueMasterMonth[];
  total: RevenueMasterMonth;
  entries: RevenueMasterEntry[];
  // Tax on Aya's fees per fee group and tax category.
  feeTax: RevenueFeeTax[] | null;
  // Invoices settled in the months shown whose Aya Master credit is missing
  // or differs.
  masterIssues: number;
  issueInvoices: RevenueInvoice[] | null;
}

export interface RevenueFeeTax {
  feeGroup: string;
  taxType: string;
  province: string;
  taxRate: number;
  collected: number;
  refunded: number;
  paidOut: number;
  outstanding: number;
  lineCount: number;
}

export type RevenueSweepStatus =
  | "Prepared"
  | "Approved"
  | "Sending"
  | "Sent"
  | "NeedsCheck"
  | "Failed"
  | "Cancelled";

/** One of Aya's DC Bank accounts. */
export interface RevenueSweepAccount {
  id: number;
  name: string;
  type: string;
  program: string;
  status: string;
  balance: number;
  availableBalance: number;
  /** Last 4 digits of the account number; empty when DC Bank does not give it. */
  accountLast4?: string;
}

export interface RevenueSweepAccounts {
  source: RevenueSweepAccount;
  destinations: RevenueSweepAccount[];
  ayaMasterReturn: number;
  ayaMasterTax?: number;
}

export interface RevenueSweepCandidate {
  businessGuid?: string;
  province?: string;
  invoiceGuid: string;
  invoiceNumber: string;
  invoiceType: string;
  businessName: string;
  settledDate: string;
  component: string;
  amount: number;
  // A tax line adds up several invoices: their settled days, how many, and
  // each one's share.
  settledFrom?: string;
  settledTo?: string;
  invoiceCount?: number;
  taxInvoices?: RevenueSweepTaxInvoice[] | null;
}

/** One invoice's share of a line of tax. */
export interface RevenueSweepTaxInvoice {
  businessGuid: string;
  component: string;
  province: string;
  invoiceNumber: string;
  settledDate: string;
  taxableAmount: number;
  taxRate: number;
  taxAmount: number;
}

export interface RevenueSweepSkipped {
  businessGuid?: string;
  invoiceNumber: string;
  businessName: string;
  component?: string;
  amount: number;
  reason: string;
  sweepNumber?: string;
}

/** What is owed for one tax category, from fntax_obligations. */
export interface RevenueTaxObligation {
  taxType: string;
  province: string;
  taxRate: number;
  taxHolder: string;
  collected: number;
  refunded: number;
  paidOut: number;
  outstanding: number;
}

export interface RevenueSweepPreview {
  kind: string;
  source: string;
  provinces: string[] | null;
  components: string[];
  from: string;
  to: string;
  lines: RevenueSweepCandidate[];
  // The lines left out by the super admin: shown, not moved.
  leftOut?: RevenueSweepCandidate[] | null;
  amount: number;
  byComponent: Record<string, number>;
  invoices: number;
  alreadyMoved: RevenueSweepSkipped[];
  notReceived: RevenueSweepSkipped[];
  obligations: RevenueTaxObligation[];
}

export interface RevenueSweepLine {
  guid: string;
  invoiceNumber: string;
  businessName: string;
  component: string;
  province: string | null;
  amount: number;
  releasedAt: string | null;
}

/** A sweep of revenue or tax out of Aya Master. Amounts in dollars. */
export interface RevenueSweep {
  guid: string;
  referenceNumber: number;
  sweepNumber: string;
  kind: string;
  source: string;
  components: string;
  provinces: string | null;
  periodFrom: string;
  periodTo: string;
  amount: number;
  status: RevenueSweepStatus;
  sourceAccountId: number;
  sourceAccountName: string;
  destinationAccountId: number;
  destinationAccountName: string;
  note: string | null;
  preparedByName: string | null;
  preparedAt: string;
  sendingAt: string | null;
  walletDebitedAt: string | null;
  sentAt: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
  failureReason: string | null;
  resolvedAt: string | null;
  resolutionNote: string | null;
  lines?: RevenueSweepLine[];
  // A tax sweep from Aya Master or the business buckets: the invoices its tax
  // came from.
  taxInvoices?: RevenueSweepTaxInvoice[] | null;
}

export interface RevenueState {
  months: RevenueSummary | null;
  monthsLoading: boolean;
  monthsError: string;

  days: RevenueSummary | null;
  daysMonth: string;
  daysLoading: boolean;
  daysError: string;

  range: RevenueSummary | null;
  // "from/to" of the range shown, so another range's rows never show.
  rangeKey: string;
  rangeLoading: boolean;
  rangeError: string;

  invoices: RevenueInvoiceList | null;
  invoicesLoading: boolean;
  invoicesError: string;

  pending: RevenueInvoiceList | null;
  pendingLoading: boolean;
  pendingError: string;

  master: RevenueMasterSummary | null;
  masterLoading: boolean;
  masterError: string;

  sweeps: RevenueSweep[];
  // The prepay tax payouts (sweeps of source PrepaySpendTax), on their own screen.
  prepayPayouts: RevenueSweep[];
  prepayPayoutsLoading: boolean;
  prepayPayoutsError: string;
  sweepsLoading: boolean;
  sweepsError: string;
  sweepAccounts: RevenueSweepAccounts | null;
  sweepAccountsError: string;
  sweepPreview: RevenueSweepPreview | null;
  sweepPreviewLoading: boolean;
  sweepPreviewError: string;
  selectedSweep: RevenueSweep | null;
  sweepRunning: boolean;
  sweepDone: string;
  sweepError: string;

  selected: RevenueInvoiceDetail | null;
  selectedLoading: boolean;
  selectedError: string;

  actionRunning: boolean;
  actionDone: string;
  actionError: string;
}

const initialState: RevenueState = {
  months: null,
  monthsLoading: false,
  monthsError: "",

  days: null,
  daysMonth: "",
  daysLoading: false,
  daysError: "",

  range: null,
  rangeKey: "",
  rangeLoading: false,
  rangeError: "",

  invoices: null,
  invoicesLoading: false,
  invoicesError: "",

  pending: null,
  pendingLoading: false,
  pendingError: "",

  master: null,
  masterLoading: false,
  masterError: "",

  sweeps: [],
  prepayPayouts: [],
  prepayPayoutsLoading: false,
  prepayPayoutsError: "",
  sweepsLoading: false,
  sweepsError: "",
  sweepAccounts: null,
  sweepAccountsError: "",
  sweepPreview: null,
  sweepPreviewLoading: false,
  sweepPreviewError: "",
  selectedSweep: null,
  sweepRunning: false,
  sweepDone: "",
  sweepError: "",

  selected: null,
  selectedLoading: false,
  selectedError: "",

  actionRunning: false,
  actionDone: "",
  actionError: "",
};

const revenueReducer = (
  state: RevenueState = initialState,
  action: Action
): RevenueState => {
  switch (action.type) {
    case "FETCH_REVENUE_MONTHS":
      return { ...state, monthsLoading: true, monthsError: "" };
    case "FETCH_REVENUE_DAYS":
      return {
        ...state,
        daysLoading: true,
        daysError: "",
        // Another month's days must not show under this month's heading.
        days: state.daysMonth === action.data ? state.days : null,
        daysMonth: action.data,
      };
    case "FETCH_REVENUE_RANGE": {
      const rangeKey = `${action.data.from}/${action.data.to}`;
      return {
        ...state,
        rangeLoading: true,
        rangeError: "",
        range: state.rangeKey === rangeKey ? state.range : null,
        rangeKey,
      };
    }
    case "FETCH_REVENUE_INVOICES":
      return { ...state, invoicesLoading: true, invoicesError: "" };
    case "FETCH_REVENUE_PENDING":
      return { ...state, pendingLoading: true, pendingError: "" };
    case "FETCH_REVENUE_MASTER":
      return { ...state, masterLoading: true, masterError: "" };
    case "FETCH_REVENUE_SWEEPS":
      return { ...state, sweepsLoading: true, sweepsError: "" };
    case "FETCH_PREPAY_TAX_PAYOUTS":
      return { ...state, prepayPayoutsLoading: true, prepayPayoutsError: "" };
    case "FETCH_REVENUE_SWEEP_ACCOUNTS":
      return { ...state, sweepAccountsError: "" };
    case "PREVIEW_REVENUE_SWEEP":
      return { ...state, sweepPreviewLoading: true, sweepPreviewError: "", sweepDone: "", sweepError: "" };
    case "FETCH_REVENUE_SWEEP":
      return {
        ...state,
        selectedSweep: state.selectedSweep?.guid === action.data ? state.selectedSweep : null,
      };
    case "PREPARE_REVENUE_SWEEP":
    case "RUN_REVENUE_SWEEP_ACTION":
      return { ...state, sweepRunning: true, sweepDone: "", sweepError: "" };
    case "FETCH_REVENUE_INVOICE":
      return {
        ...state,
        selectedLoading: true,
        selectedError: "",
        selected:
          state.selected?.invoice.invoiceNumber === action.data
            ? state.selected
            : null,
      };
    case "FLAG_REVENUE_INVOICE":
      return { ...state, actionRunning: true, actionDone: "", actionError: "" };
    case "SET_REVENUE":
      return { ...state, ...action.data };
    case "CLEAR_REVENUE_MESSAGE":
      return { ...state, actionDone: "", actionError: "" };
    default:
      return state;
  }
};

export default revenueReducer;
